<template>
   <LoadingPage v-if="isBuildingTable"></LoadingPage>
   <div class="card">
      <div class="card-body m-2">
         <!-- BDM Unassigned Warning Banner -->
         <div v-if="lead && (!lead.assigned_user_id || lead.assigned_user_id === 0)" class="alert alert-warning animate__animated animate__pulse animate__infinite_once mb-4 d-flex align-items-center" style="background: rgba(251, 191, 36, 0.08); border: 1px solid rgba(251, 191, 36, 0.4); border-left: 4px solid #fbbf24; border-radius: 8px; padding: 15px 20px; gap: 15px; color: #b45309; font-size: 0.9rem; line-height: 1.5; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);">
            <i class="fa-solid fa-triangle-exclamation" style="font-size: 1.5rem; color: #fbbf24;"></i>
            <div style="flex-grow: 1;">
               <strong style="font-size: 1rem; display: block; margin-bottom: 2px;">BDM Assignment Required</strong>
               <template v-if="lead.has_auth_organisation === 1 || (lead.organisation && lead.organisation.has_auth_organisation === 1)">
                  This lead is synchronized from the Auth service. To tag/update its BDM, please edit the Assigned BDM on the
                  <router-link :to="{ name: 'clients-dashboard', query: { organisation_uuid: lead.organisation_uuid } }" class="fw-bold alert-link text-decoration-underline" style="color: #92400e;">Organisation page</router-link>.
               </template>
               <template v-else>
                  To assign a BDM, please edit the
                  <router-link :to="'/leads?id=' + lead.id" class="fw-bold alert-link text-decoration-underline" style="color: #92400e;">Lead Details</router-link>.
               </template>
            </div>
         </div>

         <div class="row">
            <div class="collapse-transition"
               :class="[isCollapsed ? 'col-12' : (isPricingTableActive ? 'col-5' : 'col-8')]">
               <div class="card">
                  <div class="spark-doc-header">
                     <div class="spark-doc-header__left">
                        <router-link :to="{ name: 'proposals_v2' }" class="spark-doc-header__back-btn" title="Back to Proposals">
                           <i class="fa-solid fa-arrow-left"></i>
                        </router-link>
                        <h5 class="spark-doc-header__title">
                           <strong>Proposal</strong>
                           <span v-if="proposalStatus !== null" class="spark-pill-badge"
                              :class="getProposalStatusBadgeClass(proposalStatus)"
                              :title="sentAtTooltip">
                              <i :class="getProposalStatusIcon(proposalStatus)"></i>
                              {{ getStatusText(proposalStatus) }}
                              <template v-if="sentTimeAgo && (proposalStatus == 1 || proposalStatus == 2 || proposalStatus == '1' || proposalStatus == '2')">
                                 • {{ sentTimeAgo }}
                              </template>
                           </span>

                           <span v-if="renewed_from_proposal_uuid" class="spark-pill-badge spark-pill-badge--reissued"
                              style="cursor: pointer;"
                              title="View previous proposal" @click="openPreviousProposal()">
                              <i class="fa-solid fa-rotate-left"></i> Re-Issued
                           </span>

                           <span v-if="is_extended" class="spark-pill-badge spark-pill-badge--extended"
                              title="Expiry date has been extended">
                              <i class="fa-solid fa-calendar-plus"></i> Extended
                           </span>
                        </h5>
                     </div>

                     <div class="spark-doc-header__right">
                        <button v-if="!this.$store.state.auth.isGeneralUser && proposalStatus == 0 && !this.$store.state.auth.isConsultant"
                           @click="$event.stopPropagation(); this.handleRefreshPricing()"
                           :disabled="isRefreshing"
                           type="button" class="action-btn-circle action-btn-circle--sync"
                           title="Refresh Pricing with Database Catalog">
                           <i class="fa-solid fa-rotate" :class="{ 'fa-spin': isRefreshing }"></i>
                        </button>

                        <button v-if="!this.$store.state.auth.isGeneralUser" type="button" class="action-btn-circle action-btn-circle--primary"
                           title="Preview Proposal" @click="$event.stopPropagation(); this.viewProposal()">
                           <i class="fa-solid fa-arrow-up-right-from-square"></i>
                        </button>

                        <button v-if="!this.$store.state.auth.isGeneralUser" type="button" class="action-btn-circle action-btn-circle--primary"
                           title="Export PDF" @click.stop="downloadProposalPdf">
                           <i class="fa-solid fa-file-pdf"></i>
                        </button>

                        <button :disabled="proposalStatus == 3" v-if="!this.$store.state.auth.isGeneralUser"
                           type="button" class="action-btn-circle action-btn-circle--primary" title="Save Proposal"
                           @click="$event.stopPropagation(); this.saveContent([])">
                           <i class="fa-solid fa-floppy-disk"></i>
                        </button>

                        <button :disabled="proposalStatus == 3 || proposalStatus == 4 || !canSend"
                           v-if="!this.$store.state.auth.isGeneralUser && this.$store.getters['auth/canSendProposal']" type="button" class="action-btn-circle action-btn-circle--primary"
                           :title="(proposalStatus == 3) ? 'Proposal already accepted' : (proposalStatus == 4 ? 'Proposal declined' : 'Send Proposal')" @click="$event.stopPropagation(); openSendModal()">
                           <i class="fa-solid fa-paper-plane"></i>
                        </button>

                        <button v-if="!this.$store.state.auth.isGeneralUser" type="button"
                           class="action-btn-circle action-btn-circle--primary"
                           :title="isCollapsed ? 'Show Sidebar' : 'Hide Sidebar (Full Width)'"
                           @click="isCollapsed = !isCollapsed">
                           <i :class="[{ 'fa-solid fa-table-columns': isCollapsed, 'fa-solid fa-expand': !isCollapsed }]"></i>
                        </button>
                     </div>
                  </div>
                  <div class="row">
                     <WysiwygEditor :apiKey="tiny_mce_api_key" :css_code="css_code" :showPricingTableButton="proposalStatus != 3 && proposalStatus != '3'"
                        @open-pricing-table-modal="openPricingTableModal" @component-loaded="renderContent()"
                        @has-unsaved-changes="handleUnsavedChanges" @editor-ready="onEditorReady"
                        :disabled="proposalStatus == 3 || proposalStatus == '3'" ref="editorRef">
                     </WysiwygEditor>
                  </div>
               </div>
            </div>
            <div class="collapsible-content"
               :class="[isCollapsed ? 'd-none' : (isPricingTableActive ? 'col-7' : 'col-4')]"
               style="margin-top: -20px;">
               <div class="card d-flex flex-column" :style="{ visibility: isCollapsed ? 'hidden' : 'visible' }">
                  <div class="p-2 border-bottom bg-light">
                     <ul class="spark-inspector-nav">
                        <li class="spark-inspector-nav__item">
                           <button type="button" class="spark-inspector-nav__link" :class="{ active: isOption === 'Details' }"
                              @click.prevent="ChangeOption('Details')" title="Proposal Details">
                              <i class="fa-solid fa-circle-info"></i>
                              <span>Details</span>
                           </button>
                        </li>
                        <li class="spark-inspector-nav__item" v-if="proposalStatus !== 3">
                           <button type="button" class="spark-inspector-nav__link" :class="{ active: isOption === 'Content' }"
                              @click.prevent="ChangeOption('Content')" title="Content Blocks">
                              <i class="fa-solid fa-cubes"></i>
                              <span>Blocks</span>
                           </button>
                        </li>
                        <li class="spark-inspector-nav__item">
                           <button type="button" class="spark-inspector-nav__link" :class="{ active: isOption === 'Activity' }"
                              @click.prevent="ChangeOption('Activity')" title="Activity Log">
                              <i class="fa-solid fa-clock-rotate-left"></i>
                              <span>Activity</span>
                           </button>
                        </li>
                        <li class="spark-inspector-nav__item" v-if="proposalStatus === 3">
                           <button type="button" class="spark-inspector-nav__link" :class="{ active: isOption === 'Accepted' }"
                              @click.prevent="ChangeOption('Accepted')" title="Accepted Details">
                              <i class="fa-solid fa-circle-check"></i>
                              <span>Accepted</span>
                           </button>
                        </li>
                        <li class="spark-inspector-nav__item" v-if="proposalStatus === 3">
                           <button type="button" class="spark-inspector-nav__link" :class="{ active: isOption === 'Contracts' }"
                              @click.prevent="ChangeOption('Contracts')" title="Linked Contracts">
                              <i class="fa-solid fa-file-signature"></i>
                              <span>Contracts</span>
                              <span v-if="linkedContracts.length > 0" class="spark-tab-badge bg-info text-white">{{ linkedContracts.length }}</span>
                           </button>
                        </li>
                        <li class="spark-inspector-nav__item" v-if="proposalStatus !== 3">
                           <button type="button" class="spark-inspector-nav__link" :class="{ active: isOption === 'Sequence' }"
                              @click.prevent="ChangeOption('Sequence')" title="Sequence">
                              <i class="fa-solid fa-arrow-down-1-9"></i>
                              <span>Sequence</span>
                           </button>
                        </li>
                     </ul>
                  </div>
                  <div v-show="isOption === 'Details'">
                     <ProposalDetailsForm ref="proposalDetailsFormRef" @onInitializeDetails="setItems()" @toggleDiscountCalculation="onToggleDiscountCalculation" />
                  </div>
                  <div v-show="isOption === 'Content'">
                     <ProposalVariableFields ref="proposalVariableFieldsRef" :setItems="itemsForSettings"
                        :isLoading="isBuildingTable" @pricingTableInActive="setPricingTableInActive()"
                        @pricingTableActive="setPricingTableActive()" @pricing-table-changes="onPricingChange" />
                  </div>
                  <div v-show="isOption === 'Activity'" style="max-height:1000px; overflow-y:auto;">
                     <ProposalActivity ref="proposalActivityRef" />
                  </div>
                  <div v-show="isOption === 'Sequence'" style="max-height:1000px; overflow-y:auto;">
                     <ProposalSequence ref="proposalSequenceRef" :proposalId="proposal_id" :initialSections="proposal_sequence"
                        @sequence-updated="renderSequence(proposal_id)" />
                  </div>
                  <div v-show="isOption === 'Accepted'">
                     <ProposalAcceptedDetails :details="acceptedDetails" />
                  </div>
                  <div v-show="isOption === 'Contracts'">
                     <div v-if="isLoadingContracts" class="text-center py-4">
                        <i class="fa-solid fa-spinner fa-spin me-2"></i> Loading contracts...
                     </div>
                     <div v-else-if="linkedContracts.length === 0" class="text-center py-4 text-muted">
                        <i class="fa-solid fa-file-contract" style="font-size:32px; opacity:0.3;"></i>
                        <p class="mt-2">No contracts have been created for this proposal yet.</p>
                     </div>
                     <div v-else class="linked-contracts-list">
                        <div v-for="contract in linkedContracts" :key="contract.id" class="linked-contract-card">
                           <div class="d-flex justify-content-between align-items-center mb-2">
                              <strong>{{ contract.contract_title || 'Untitled Contract' }}</strong>
                              <span class="linked-contract-status" :style="{ backgroundColor: getContractStatusColor(contract.contract_status) }">
                                 {{ getContractStatusText(contract.contract_status) }}
                              </span>
                           </div>
                           <div class="d-flex gap-3 mb-2 align-items-center" style="font-size:12px; color:#718096;">
                              <span v-if="contract.contract_version" class="spark-pill-badge" :class="getVersionBadgeClass(contract.contract_version)">
                                 {{ contract.contract_version }}
                              </span>
                              <span><i class="fa-regular fa-calendar me-1"></i>{{ formatDate(contract.created_at) }}</span>
                           </div>
                           <button class="btn btn-sm btn-outline-info" @click="openContractInNewTab(contract)">
                              <i class="fa-solid fa-arrow-up-right-from-square me-1"></i> Open Contract
                           </button>
                        </div>
                     </div>
                  </div>
               </div>
               <div style="clear: both;"></div>
            </div>
         </div>
      </div>
   </div>

   <!-- ========== START - FORM ========== -->
   <div class="modal fade" id="sendForm" tabindex="-1" data-bs-keyboard="false" data-bs-backdrop="static" ref="sendForm"
      style="top: 2%;">
      <div ref="modalSendFormDialogRef" class="modal-dialog modal-dialog-scrollable modal-lg">
         <div class="modal-content shadow-lg border-0" style="border-radius: 16px; background-color: #fff;">
            <div class="modal-header border-0 pb-0 justify-content-end" style="background-color: transparent;">
               <div>
                  <button ref="closeSendFormModal" type="button" class="btn-close" data-bs-dismiss="modal"
                     aria-label="Close"></button>
               </div>
            </div>

            <div class="modal-body p-4 pt-0">
               <SendForm ref="sendProposalForm" :initialSendTo="proposal_client_email" :proposalID="proposal_id"
                  :hasEditor="true" :initialProposalTitle="proposal_title"
                  @proposalSentSuccess="this.init(); this.$refs.closeSendFormModal.click(); this.setItems();" />
            </div>
         </div>
      </div>
   </div>
   <!-- ========== END - FORM ========== -->


   <div id="pdf-content-area" :style="pdfContentAreaStyle">
      <div class="pdf-page-section">
         <h2 class="pdf-title">{{ proposal_title }}</h2>
      </div>
      <div v-if="cover_page_content" class="pdf-page-section" v-html="cover_page_content"></div>
      <div v-if="product_service_content" class="pdf-page-section" v-html="product_service_content"></div>
      <div v-if="filteredProposalItems.length > 0" class="pdf-page-section pdf-pricing-section">
         <div v-for="(pi, optionIndex) in filteredProposalItems" :key="'pdf-option-' + pi.option_index">
            <h4 class="pdf-section-title">
               Vertex Products - {{ (pi.option_index == 1) ? 'Main Option' : 'Option ' +
                  toOptionName(pi.option_index) }}
            </h4>
            <table class="pdf-product-table">
               <thead>
                  <tr>
                     <th>Product</th>
                     <th>Description</th>
                     <th>Notes</th>
                     <th>Quantity</th>
                     <th>Price</th>
                     <th v-if="show_discount_calculation">Price Discount</th>
                     <th v-if="show_discount_calculation">Total Discount</th>
                     <th>Total AUD</th>
                  </tr>
               </thead>
               <tbody>
                  <tr v-for="(item, itemIndex) in pi.productList"
                     :key="'pdf-item-' + pi.option_index + '-' + itemIndex">
                     <td>
                        <strong>{{ "none" || item.Product.display_name }}</strong>
                     </td>
                     <td>{{ item.description }}</td>
                     <td>{{ item.remarks }}</td>
                     <td>
                        <span>{{ item.quantity }}</span>
                     </td>
                     <td>
                        <span v-if="!show_discount_calculation">{{
                           formatCurrency(item.price -
                              item.discount) }}</span>
                        <span v-else>{{ formatCurrency(item.price) }}</span>
                     </td>
                     <td v-if="show_discount_calculation">
                        <span>{{ formatCurrency(item.discount) }}</span>
                     </td>
                     <td v-if="show_discount_calculation">
                        <span>{{ formatCurrency(item.discount * item.quantity) }}</span>
                     </td>
                     <td>{{ formatCurrency(calculateTotal(item)) }}</td>
                  </tr>
               </tbody>
               <tfoot>
                  <tr class="pdf-total-row">
                     <td :colspan="show_discount_calculation ? 7 : 5" class="pdf-total-label"
                        style="text-align: right; padding-right: 15px;">Subtotal</td>
                     <td class="pdf-total-amount" style="text-align: right;">{{
                        formatCurrency(calculateOptionSubtotalPrice(pi.productList)) }}</td>
                  </tr>
                  <tr class="pdf-total-row">
                     <td :colspan="show_discount_calculation ? 7 : 5" title="Goods and Services Tax"
                        class="pdf-total-label" style="text-align: right; padding-right: 15px;">GST (10%)</td>
                     <td class="pdf-total-amount" style="text-align: right;">{{
                        formatCurrency(calculateOptionGST(pi.productList)) }}</td>
                  </tr>
                  <tr class="pdf-total-row">
                     <td :colspan="show_discount_calculation ? 7 : 5" class="pdf-total-label"
                        style="text-align: right; padding-right: 15px;">Total</td>
                     <td class="pdf-total-amount" style="text-align: right;">{{
                        formatCurrency(calculateOptionTotalPrice(pi.productList)) }}</td>
                  </tr>
               </tfoot>
            </table>
            <br v-if="optionIndex < filteredProposalItems.length - 1" />
         </div>
      </div>

      <div v-if="content_conflict" class="pdf-page-section" v-html="content_conflict"></div>

      <div v-if="content_insurance" class="pdf-page-section" v-html="content_insurance"></div>

      <div v-if="content_whs_responsibilities" class="pdf-page-section" v-html="content_whs_responsibilities"></div>

      <div v-if="content_terms_conditions" class="pdf-page-section" v-html="content_terms_conditions"></div>

   </div>

   <ConfirmModal
      v-model="showConfirmModal"
      :title="confirmModalTitle"
      :message="confirmModalMessage"
      @confirm="onConfirmAction"
   />
</template>

<script>
import moment from 'moment'
import { truncateLongText, log, isEmpty } from '@/plugins/helper.js'
import { isAustralianCountry } from '@/plugins/businessValidator.js'
import WysiwygEditor from '@/components/Utilities/WysiwygEditor.vue'
//import BlockFormEdit from '@/components/ContentLibrary/BlockFormEdit.vue'
import ProposalVariableFields from '@/components/Proposals/ProposalVariableFields.vue'
import ProposalDetailsForm from '@/components/Proposals/ProposalDetailsForm.vue'
import ProposalActivity from '@/components/Proposals/ProposalActivity.vue'
import ProposalSequence from '@/components/Proposals/ProposalSequence.vue';
import ProposalAcceptedDetails from '@/components/Proposals/ProposalAcceptedDetails.vue';
import { nextTick } from 'vue'; // Import nextTick
import SendForm from '@/components/Proposals/SendForm.vue';
import { Modal } from 'bootstrap'; // Import Bootstrap's Modal class
import LoadingPage from '@/components/Utilities/vertex-loading.vue'
// Import PDF libraries
//import html2canvas from 'html2canvas';
//import jsPDF from 'jspdf';

export default ({
   components: {
      WysiwygEditor,
      //BlockFormEdit,
      ProposalVariableFields,
      ProposalDetailsForm,
      ProposalActivity,
      SendForm,
      LoadingPage,
      ProposalAcceptedDetails,
      ProposalSequence
   },

   props: {
      id: {
         type: String,
      },
   },

   data() {
      return {
         isBuildingTable: false,
         currentBlockType: null,
         blockData: { /* Your block data here */ }, // Data to pass to your blocks
         componentToRender: {

         },
         block: {
            id: "",
            dateCreated: null,
            blockName: '',
            blockType: '',
            blockUse: '',
            defaultVersion: true,
            color: "#4682B4",
            products: [],
            content: "",
         },
         blockTypes: [],
         blockUse: [],
         productList: [],
         isEditing: false,
         disabledWhenClicked: false,
         sendProposalForm: null,
         isDownloading: false,
         debounceFn: null,
         //currentBlockType: "none",
         currentBlockName: "none",
         tiny_mce_api_key: "",
         css_code: "",
         isCollapsed: false,
         isOption: "Details",
         itemsForSettings: {},
         variableFieldsMapping: [
            {
               replace_string: `&nbsp;<mark>{{<span style="color: red;">!*</span><span id="variable_value_organisation_name">` + "organisation_name" + `</span><span style="color: red;">*!</span>}}</mark>`,
               replace_variable: "organisation_name"
            },
            {
               replace_string: `&nbsp;<mark>{{<span style="color: red;">!*</span><span id="variable_value_abn">` + "abn" + `</span><span style="color: red;">*!</span>}}</mark>`,
               replace_variable: "abn"
            },
            {
               replace_string: `&nbsp;<mark>{{<span style="color: red;">!*</span><span id="variable_value_business_number">` + "business_number" + `</span><span style="color: red;">*!</span>}}</mark>`,
               replace_variable: "business_number"
            },
            {
               replace_string: `&nbsp;<mark>{{<span style="color: red;">!*</span><span id="variable_value_business_number_type">` + "business_number_type" + `</span><span style="color: red;">*!</span>}}</mark>`,
               replace_variable: "business_number_type"
            },
            {
               replace_string: `&nbsp;<mark>{{<span style="color: red;">!*</span><span id="variable_value_business_number_formatted">` + "business_number_formatted" + `</span><span style="color: red;">*!</span>}}</mark>`,
               replace_variable: "business_number_formatted"
            },
            {
               replace_string: `<mark>{{<span style="color: red;">!*</span><span id="variable_value_contact_number">` + "contact_number" + `</span><span style="color: red;">*!</span>}}</mark>`,
               replace_variable: "phone_number"
            },
            {
               replace_string: `<mark>{{<span style="color: red;">!*</span><span id="variable_value_org_address">` + "org_address" + `</span><span style="color: red;">*!</span>}}</mark>`,
               replace_variable: "org_address"
            },
            {
               replace_string: `<mark>{{<span style="color: red;">!*</span><span id="variable_value_first_name">` + "first_name" + `</span><span style="color: red;">*!</span>}}</mark>`,
               replace_variable: "first_name"
            },
            {
               replace_string: `<mark>{{<span style="color: red;">!*</span><span id="variable_value_last_name">` + "last_name" + `</span><span style="color: red;">*!</span>}}</mark>`,
               replace_variable: "last_name"
            },
            {
               replace_string: `<mark>{{<span style="color: red;">!*</span><span id="variable_value_relations_manager">` + "relations_manager" + `</span><span style="color: red;">*!</span>}}</mark>`,
               replace_variable: "relations_manager"
            },
            {
               replace_string: `<mark>{{<span style="color: red;">!*</span><span id="variable_value_msp">` + "msp" + `</span><span style="color: red;">*!</span>}}</mark>`,
               replace_variable: "msp"
            },
            {
               replace_string: `<mark>{{<span style="color: red;">!*</span><span id="variable_value_billing_email">` + "billing_email" + `</span><span style="color: red;">*!</span>}}</mark>`,
               replace_variable: "email"
            },
            {
               replace_string: `<mark>{{<span style="color: red;">!*</span><span id="variable_value_organisation_trading_name">` + "organisation_trading_name" + `</span><span style="color: red;">*!</span>}}</mark>`,
               replace_variable: "organisation_name"
            },
            {
               replace_string: `<mark>{{<span style="color: red;">!*</span><span id="variable_value_client_address_for_service_of_notices">` + "client_address_for_service_of_notices" + `</span><span style="color: red;">*!</span>}}</mark>`,
               replace_variable: "org_address"
            },
            {
               replace_string: `<mark>{{<span style="color: red;">!*</span><span id="variable_value_client_email_address_for_service_of_notices">` + "client_email_address_for_service_of_notices" + `</span><span style="color: red;">*!</span>}}</mark>`,
               replace_variable: "email"
            }
         ],
         hasNoMinor: false,
         isPricingTableActive: false,
         proposal_client_email: "",
         proposal_id: "",
         proposal_sequence: [],
         proposalStatus: null, // Add this to hold the current status reactively
         sentAtState: null,
         colorForStatus: {
            pending: '#F3994D', // Orange
            sent: '#00a0c1',    // Blue
            resent: '#00a0c1',   // Blue
            accepted: '#00A54F',// Green
            rejected: '#EB1C24' // Red
         },
         proposal_title: "",
         content_conflict: "",
         content_insurance: "",
         content_terms_conditions: "",
         content_whs_responsibilities: "",
         product_service_content: "",
         cover_page_content: "",
         show_discount_calculation: false,
         filteredProposalItems: [],
         pdfContentAreaStyle: {
            position: 'absolute',
            left: '-9999px',
            top: 0,
            width: '210mm',
            padding: '20mm',
            backgroundColor: 'white',
            boxSizing: 'border-box'
         },
         canSend: true,
         acceptedDetails: {},
         editorReady: false,
         isEditorReady: false,
         isDataReady: false,
         pendingContent: null,
         hasUnsavedPricingChanges: false,
         proposalSections: [],
         is_extended: false,
         isRefreshing: false,
         linkedContracts: [],
         isLoadingContracts: false,
         showConfirmModal: false,
         confirmModalTitle: '',
         confirmModalMessage: '',
         pendingAction: null,
      }
   },

   created() {
      this.tiny_mce_api_key = process.env.VUE_APP_TINY_MCE_API_KEY
      this.css_code = `
          /* --- Your Existing Grid & Typography Styles --- */
          .image-grid {
             display: grid;
             grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
             grid-gap: 10px;
          }
 
          .grid-item {
             overflow: hidden;
             border: 3px solid black; 
          }
 
          .grid-item img {
             width: 100%;
             height: auto;
             display: block;
          }
 
          body { 
             font-family: "Roboto", sans-serif; 
             font-size: 16px; 
          }

         .orange-label-product-option {
            display: inline-block;
            background-color: #ff8c00;
            color: white;
            padding: 6px 12px;
            border-radius: 5px;
            font-weight: bold;
            font-size: 0.95em;
            text-transform: uppercase;
            letter-spacing: 0.5px;
         }

         /* --- Bulletproof Proposal Section Layout Styles --- */
         
         /* 1. Prevent wrappers from expanding beyond the editor width */
         .non-editable-section, .editable-content-inside {
            box-sizing: border-box !important;
            max-width: 100% !important;
         }
          
         /* 2. Force ALL generic images to be responsive so they don't break the dashed boxes */
         .editable-content-inside img, .non-editable-section img {
            max-width: 100% !important;
            height: auto !important;
            object-fit: contain;
         }

         /* 3. Prevent long unbroken text (like URLs) from stretching the box horizontally */
         .editable-content-inside, .editable-content-inside p, .editable-content-inside div {
            word-wrap: break-word !important;
            overflow-wrap: break-word !important;
         }

         /* 4. If a user inserts a massive custom table, add a scrollbar to the box rather than breaking the layout */
         .editable-content-inside {
            overflow-x: auto;
         }

         mark {
            background-color: transparent !important;
            color: inherit !important;
         }

         .btn-open-pricing-modal {
            background-color: #00A0C1 !important;
            color: #ffffff !important;
            border: 1px solid #008fae !important;
            padding: 4px 12px !important;
            border-radius: 4px !important;
            font-size: 13px !important;
            font-weight: 600 !important;
            cursor: pointer !important;
            display: inline-flex !important;
            align-items: center !important;
            gap: 6px !important;
            box-shadow: 0 2px 4px rgba(0,0,0,0.08) !important;
            transition: all 0.2s ease !important;
            text-decoration: none !important;
         }

         .btn-open-pricing-modal:hover {
            background-color: #008fae !important;
            box-shadow: 0 3px 6px rgba(0,0,0,0.15) !important;
         }
`;
   },

   mounted() {
      this.init()
      this.sendProposalForm = new Modal(this.$refs.sendForm);
   },

   computed: {
      item() {
         return this.id
      },
      lead() {
         return this.$refs.proposalDetailsFormRef ? this.$refs.proposalDetailsFormRef.lead : null;
      },
      sentTimeAgo() {
         if (!this.sentAtState) return '';
         if (!moment(this.sentAtState).isValid()) return '';
         return moment(this.sentAtState).fromNow();
      },
      sentAtTooltip() {
         if (!this.sentAtState) return '';
         return `Last sent: ${this.formatDate(this.sentAtState)} (${moment(this.sentAtState).fromNow()})`;
      },
   },

   watch: {
      // item() {
      //    this.init()
      // },
   },

   methods: {
      renderSequence(proposalId) {
         this.$refs.proposalDetailsFormRef.editProposal(proposalId)
      },

      async handleRefreshPricing() {
         this.pendingAction = 'refreshPricing';
         this.confirmModalTitle = 'Refresh Pricing';
         this.confirmModalMessage = 'Are you sure you want to refresh the pricing? This will overwrite the prices with the latest catalog rates (while preserving discounts).';
         this.showConfirmModal = true;
      },

      async onConfirmAction() {
         const action = this.pendingAction;
         this.pendingAction = null;
         if (action !== 'refreshPricing') return;

         this.isRefreshing = true;
         try {
            const response = await this.$axios.post('/proposals/' + this.proposal_id + '/refresh-pricing');
            if (response.data.success) {
               this.$toast.success(response.data.message);
               this.init();
            } else {
               this.$toast.error(response.data.message || 'Failed to refresh pricing.');
            }
         } catch (error) {
            console.error('Error refreshing pricing:', error);
            this.$toast.error('An error occurred during refresh.');
         } finally {
            this.isRefreshing = false;
         }
      },

      async openPreviousProposal() {
         if (!this.renewed_from_proposal_uuid) return;

         try {
            // 1. Fetch the keys for this specific UUID from your backend
            const response = await this.$axios.get(`/proposals/${this.renewed_from_proposal_uuid}`);
            const data = response.data.vertex_proposal;

            // 2. Resolve the existing client route using the fetched keys
            const routeData = this.$router.resolve({
               name: 'clientProposal',
               params: { unique_key: data.unique_key, access_key: data.internal_key }
            });

            // 3. Open the route in a brand new tab!
            window.open(routeData.href, '_blank');

         } catch (error) {
            this.$toast.error("Could not load previous proposal details.");
            console.error(error);
         }
      },

      onPricingChange(hasChanges) {
         this.hasUnsavedPricingChanges = hasChanges;
      },

      calculateTotal(item) {
         // Correctly calculates (price - discount) * quantity for a single row
         const price = parseFloat(item.price) || 0;
         const discount = parseFloat(item.discount) || 0;
         const quantity = parseInt(item.quantity) || 0;
         return (price - discount) * quantity;
      },

      calculateOptionSubtotalPrice(productList) {
         // Correctly sums the total of each row to get the subtotal
         return productList.reduce((total, item) => {
            return total + this.calculateTotal(item);
         }, 0);
      },

      calculateOptionGST(productList) {
         // Calculates 10% of the correct subtotal
         return this.calculateOptionSubtotalPrice(productList) * 0.1;
      },

      calculateOptionTotalPrice(productList) {
         // Calculates the subtotal plus GST
         return this.calculateOptionSubtotalPrice(productList) * 1.1;
      },

      formatCurrency(value) {
         const numericValue = parseFloat(value);
         if (isNaN(numericValue)) {
            return '$0.00';
         }
         return `$${numericValue.toFixed(2)}`;
      },

      calculateTotalDiscount(item) {
         return (item.discount * item.quantity);
      },

      toOptionName(count) {
         // Handle the special case: if count is 1, return an empty string
         if (count <= 1) {
            return '';
         }

         let result = '';
         let num = count; // Start with the actual count for conversion

         while (num > 0) {
            // Calculate the remainder when dividing by 26
            // This gives us the position of the current letter (1 for A, 26 for Z)
            let remainder = num % 26;

            // Adjustment for 'Z' and subsequent columns:
            // If remainder is 0, it means the letter is 'Z' (the 26th letter)
            // and we need to decrement num by 26 to properly carry over for the next iteration.
            if (remainder === 0) {
               remainder = 26; // Map 0 remainder to the 26th letter ('Z')
            }

            // Convert the remainder to a character
            // 'A' has ASCII value 65. So, (remainder - 1) + 65 gives us the correct character code
            // (e.g., if remainder is 1, 1-1=0, 65+0=65='A')
            result = String.fromCharCode(64 + remainder) + result;

            // Update num for the next iteration
            // If remainder was 26 (meaning 'Z'), we effectively processed a full '26' group.
            // So, subtract 'remainder' and then divide by 26.
            num = Math.floor((num - remainder) / 26);
         }

         return result;
      },

      setPricingTableInActive() {
         this.isPricingTableActive = false
      },

      setPricingTableActive() {
         this.isPricingTableActive = true
      },

      openPricingTableModal() {
         if (this.proposalStatus == 3 || this.proposalStatus == '3') {
            return;
         }
         if (this.$refs.proposalVariableFieldsRef && this.$refs.proposalVariableFieldsRef.$refs.pricingTableModal) {
            this.$refs.proposalVariableFieldsRef.$refs.pricingTableModal.openModal();
         }
      },

      openSendModal() {

         if (!this.verifyCanSend())
            return

         this.sendProposalForm.show();
      },

      isProposalSent(versionNumber) {
         // 1. Ensure the version string is a string and starts with 'v'.
         if (typeof versionNumber !== 'string' || !versionNumber.startsWith('v')) {
            return false; // Invalid format or type
         }

         // 2. Remove the 'v' prefix and split the remaining string by '.'.
         //    We expect exactly two parts: major and minor.
         const parts = versionNumber.slice(1).split('.');
         if (parts.length !== 2) {
            return false; // Invalid format, not "major.minor"
         }

         const minorStr = parts[1]; // The minor version is the second part

         // 3. Attempt to convert the minor version string to an integer.
         //    parseInt() will stop at the first non-numeric character.
         //    isNaN() checks if the result is "Not a Number".
         const minor = parseInt(minorStr, 10); // Use radix 10 for decimal conversion

         // Check if parseInt resulted in NaN (meaning minorStr was not a valid number)
         // or if the original minorStr was empty, or if it contained non-numeric characters
         // after a number (e.g., "1a") which parseInt would partially parse to 1.
         // To be strict like Go's Atoi, we can check if the parsed part matches the original string
         // after conversion.
         if (isNaN(minor) || minor.toString() !== minorStr) {
            // This stricter check ensures 'v1.1a' also returns false,
            // as parseInt('1a') would yield 1, but '1'.toString() !== '1a'
            return false; // Minor version is not a valid integer representation
         }

         // 4. Check if the minor version is greater than 0.
         return minor <= 0;
      },

      addContent(content) {
         this.$refs.editorRef.addContent(content)
      },

      setSectionContentById(section_id, content) {

         if (isEmpty(content)) {
            alert("Block is empty")
            return
         }

         this.$refs.editorRef.setSectionContentById(section_id, content)
      },

      appendToSectionContentById(section_id, content) {

         if (isEmpty(content)) {
            alert("Block is empty")
            return
         }

         this.$refs.editorRef.appendToSectionContentById(section_id, content)
      },

      checkIfIDExist(id_string) {
         return this.$refs.editorRef.checkIfIDExist(id_string)
      },

      setContent(content, isDisabled) {
         this.$refs.editorRef.setContent(content, isDisabled)
      },

      setHeaderTitle(title) {
         this.$parent.title = title
      },

      getContent(content) {
         return this.$refs.editorRef.getContent(content)
      },

      clearIsDirty() {
         this.$refs.editorRef.markEditorContentClean()
      },

      setToDisabled() {
         this.$refs.editorRef.setToDisabled()
      },

      // 1. Create a coordinator function
      tryInitializeContent() {
         // Only proceed if BOTH are ready
         if (this.isEditorReady && this.isDataReady) {
            // Trigger the actual content render here
            this.renderContent();
         }
      },

      // 2. This method listens to the new @editor-ready event from WysiwygEditor
      onEditorReady() {
         this.isEditorReady = true;
         this.tryInitializeContent();
      },

      setItems() {
         nextTick(() => {
            this.itemsForSettings = this.getProposalItems()
            log("items for settings", this.itemsForSettings)
            this.hasNoMinor = this.isProposalSent(this.$refs.proposalDetailsFormRef.getVersion())
            this.proposal_client_email = this.getClientEmail()
            this.proposal_id = this.$refs.proposalDetailsFormRef.proposal.id
            this.proposal_sequence = (this.$refs.proposalDetailsFormRef.proposal.proposal_section_sequence || []).filter(seq => seq.section_name !== 'WHS')
            this.proposalStatus = this.$refs.proposalDetailsFormRef.proposal.proposal_status;
            this.sentAtState = this.$refs.proposalDetailsFormRef.proposal.sent_at_raw || this.$refs.proposalDetailsFormRef.proposal.sent_at;
            this.proposal_title = this.$refs.proposalDetailsFormRef.proposal.proposal_title;
            this.show_discount_calculation = this.$refs.proposalDetailsFormRef.proposal.show_discount_calculation;

            this.renewed_from_proposal_uuid = this.$refs.proposalDetailsFormRef.proposal.renewed_from_proposal_uuid || null;
            this.is_extended = this.$refs.proposalDetailsFormRef.proposal.is_extended || false;
            this.cover_page_content = this.$refs.proposalDetailsFormRef.proposal.proposal_section_content.cover_page_content_draft;
            this.product_service_content = this.$refs.proposalDetailsFormRef.proposal.proposal_section_content.product_service_content_draft;
            this.content_social_impact = this.$refs.proposalDetailsFormRef.proposal.proposal_section_content.content_social_impact_draft;
            this.content_commitment = this.$refs.proposalDetailsFormRef.proposal.proposal_section_content.content_commitment_draft;
            this.content_whs_responsibilities = this.$refs.proposalDetailsFormRef.proposal.proposal_section_content.content_whs_responsibilities_draft;
            this.content_terms_conditions = this.$refs.proposalDetailsFormRef.proposal.proposal_section_content.content_terms_conditions_draft;

            // Load JSON-based sections
            const jsonDraft = this.$refs.proposalDetailsFormRef.proposal.proposal_section_content.proposal_sections_json_draft;
            if (jsonDraft) {
               try { this.proposalSections = JSON.parse(jsonDraft); }
               catch (e) { this.proposalSections = []; }
            } else {
               this.proposalSections = [];
            }

            this.filterProposalItems(this.$refs.proposalDetailsFormRef.getProposalItems())

            const prop = this.$refs.proposalDetailsFormRef.proposal || {};
            const lead = prop.Lead || {};
            const acceptances = prop.contract_acceptances || [];
            const latestAcceptance = acceptances.length > 0 ? acceptances[0] : null;

            this.acceptedDetails = {
               is_accepted: prop.proposal_status === 3,
               accepted_at: prop.accepted_at,
               accepted_by_email: prop.accepted_by_email,
               accepted_by_ip: prop.accepted_by_ip,
               option_index_selected: prop.option_index_selected,
               total_accepted: prop.total_accepted,
               accepted_items: this.$refs.proposalDetailsFormRef.getProposalItems().filter(item => item.added_to_contract),
               organisation_name: (latestAcceptance && latestAcceptance.organisation_name) ? latestAcceptance.organisation_name : (lead ? lead.organisation_name : ''),
               original_organisation_name: (lead && lead.original_organisation_name) ? lead.original_organisation_name : (prop.original_organisation_name || ''),
               abn: (latestAcceptance && latestAcceptance.abn) ? latestAcceptance.abn : (lead ? (lead.abn || lead.abn_info || '') : ''),
               original_abn: (lead && lead.original_abn) ? lead.original_abn : (prop.original_abn || ''),
               organisation_uuid: lead?.organisation_uuid || lead?.auth_organisation_uuid || lead?.organisation?.uuid || prop?.organisation_uuid || '',
            };

            this.isDataReady = true;
            this.tryInitializeContent();
         })
      },

      async downloadProposalPdf() {
         const uniqueKey = this.$refs.proposalDetailsFormRef.getUniqueKey();
         const accessKey = this.$refs.proposalDetailsFormRef.getInternalAccessKey();

         this.$toast.info("Generating PDF on the server, please wait...");

         try {
            // 1. Fetch the file from your Go backend
            const response = await this.$axios.get(`/proposals/${uniqueKey}/${accessKey}/pdf`, {
               responseType: 'blob', // CRITICAL: Tells Axios not to parse this as JSON
               headers: {
                  'Accept': 'application/pdf'
               }
            });

            // 2. Safety check: Did the backend send an empty file?
            if (!response.data || response.data.size === 0) {
               this.$toast.error("Error: The server returned an empty PDF.");
               return;
            }

            // 3. Create the Blob and Download Link
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.style.display = 'none'; // Keep it hidden
            link.href = url;

            // Clean up the file name
            let fileName = this.proposal_title ? `${this.proposal_title.replace(/[^a-z0-9]/gi, '_')}.pdf` : 'Vertex_Proposal.pdf';
            link.setAttribute('download', fileName);

            // 4. Append, Click, and Cleanup
            document.body.appendChild(link);
            link.click();

            // Use a small timeout to ensure the browser registers the click before we delete the link
            setTimeout(() => {
               document.body.removeChild(link);
               window.URL.revokeObjectURL(url);
            }, 200);

            this.$toast.success("PDF downloaded successfully!");

         } catch (error) {
            console.error("Download error:", error);

            // If the backend threw a 500 error, Axios will catch it here
            if (error.response && error.response.data instanceof Blob) {
               // Because we set responseType to blob, we have to read the error message manually
               const text = await error.response.data.text();
               console.error("Backend Error Message:", text);
            }

            this.$toast.error("Failed to generate PDF on the server.");
         }
      },

      filterProposalItems(proposal_items) {
         if (!isEmpty(proposal_items)) {
            this.filteredProposalItems = [];
            const groupedItems = {};
            proposal_items.forEach((p) => {
               if (!groupedItems[p.option_index]) {
                  groupedItems[p.option_index] = { productList: [], option_index: p.option_index };
               }
               groupedItems[p.option_index].productList.push(p);
            });
            this.filteredProposalItems = Object.values(groupedItems);
            this.filteredProposalItems.sort((a, b) => a.option_index - b.option_index);
         } else {
            this.filteredProposalItems = [];
         }
      },

      getProposalItems() {
         return {
            items: this.$refs.proposalDetailsFormRef.getProposalItems(),
         }
      },

      getClientEmail() {
         return this.$refs.proposalDetailsFormRef.proposal.client_lead_email
      },

      async saveContent(productList, is_saving_items = false) {
         this.isBuildingTable = true;


         try {
            if (!this.$refs.editorRef.isEditorContentDirty() && this.$refs.proposalDetailsFormRef.isShowDiscountChanged() == true && !!is_saving_items) {
               this.$toast.info("No changes have been made.");
               return
            }

            //let proposal_content = this.getContent()

            let proposal_section_content = {
               // cover_page_content: this.getSectionContentById("start_cover_section"),
               // product_service_content: this.getSectionContentById("start_product_services_section"),
               // pricing_table_content: this.getSectionContentById("start_pricing_table_section"),
               // image_grid_content: this.getSectionContentById("start_image_grid_section"),
               // testimonials_content: this.getSectionContentById("start_testimonials_section"),
               // // content_content: this.getSectionContentById("start_content_blocks_section"),
               // content_conflict: this.getSectionContentById("start_content_conflict_section"),
               // content_insurance: this.getSectionContentById("start_content_insurance_section"),
               // content_whs_responsibilities: this.getSectionContentById("start_content_whsr_section"),
               // content_terms_conditions: this.getSectionContentById("start_content_terms_condition_section"),
               // proposal_content: proposal_content,

               cover_page_content_draft: this.getSectionContentById("start_cover_section"),
               product_service_content_draft: this.getSectionContentById("start_product_services_section"),
               pricing_table_introduction_content_draft: this.getSectionContentById("start_pricing_table_intro"),
               pricing_table_content_draft: this.getSectionContentById("start_pricing_table_section"),
               pricing_table_notes_content_draft: this.getSectionContentById("start_pricing_table_notes"),
               image_grid_content_draft: this.getSectionContentById("start_image_grid_section"),
               //testimonials_content_draft: this.getSectionContentById("start_testimonials_section"),
               // content_content: this.getSectionContentById("start_content_blocks_section"),
               content_social_impact_draft: this.getSectionContentById("start_content_social_impact_section"),
               content_commitment_draft: this.getSectionContentById("start_content_commitment_section"),
               content_whs_responsibilities_draft: this.getSectionContentById("start_content_whsr_section"),
               content_terms_conditions_draft: this.getSectionContentById("start_content_terms_condition_section"),
               content_next_steps_draft: this.getSectionContentById("start_content_next_steps_section"),
               proposal_sections_json_draft: this.proposalSections.length > 0
                  ? JSON.stringify(this.readSectionsFromEditor())
                  : '',
               proposal_title: this.proposal_title,
               //proposal_content: proposal_content,
            }

            if (!isEmpty(productList)) {
               proposal_section_content["proposal_items"] = productList
            }

            // log(proposal_section_content)
            // log(proposal_content)
            // log("product_list on save:", proposal_section_content["proposal_items"])

            this.canSend = true

            await this.$refs.proposalDetailsFormRef.updateProposal(proposal_section_content)
            this.$refs.proposalActivityRef.triggerDebounce()
         } finally {
            // Wait 2.5 seconds (2500 milliseconds) before setting it to false
            setTimeout(() => {
               this.isBuildingTable = false;
            }, 3000);
         }
      },

      async onToggleDiscountCalculation(val) {
         this.show_discount_calculation = val;
         await this.saveContent([]);
      },

      sendProposal() {

         if (this.$refs.editorRef.isEditorContentDirty() || this.$refs.proposalDetailsFormRef.isShowDiscountChanged() == false) {
            this.$toast.error("There are unsaved changes.");
            return
         }

         this.$refs.proposalDetailsFormRef.sendProposal()
         this.$refs.proposalActivityRef.triggerDebounce()
      },

      verifyCanSend() {
         if (this.lead && (!this.lead.assigned_user_id || this.lead.assigned_user_id === 0)) {
            this.$toast.error("You cannot send this proposal because the associated Lead has no BDM assigned to it.");
            this.canSend = false;
            return false;
         }

         if (this.$refs.editorRef.isEditorContentDirty() || this.$refs.proposalDetailsFormRef.isShowDiscountChanged() == false) {
            this.$toast.error("There are unsaved changes.");
            this.canSend = false
            return false
         }

         if (this.hasUnsavedPricingChanges) {
            this.$toast.error("There are unsaved pricing table changes. Please build first.");
            this.canSend = false
            return false
         }

         this.canSend = true
         return true
      },

      handleUnsavedChanges(status) {
         this.canSend = !status;
      },

      switchBlockType(blockType) {
         this.currentBlockType = blockType;
      },

      toggleBold() {
         this.isBold = !this.isBold;
      },
      toggleItalic() {
         this.isItalic = !this.isItalic;
      },
      toggleUnderline() {
         this.isUnderline = !this.isUnderline;
      },

      init() {
         this.$refs.proposalDetailsFormRef.editProposal(this.id)
         this.$refs.proposalActivityRef.init(this.id)
         this.fetchLinkedContracts()
      },

      async fetchLinkedContracts() {
         this.isLoadingContracts = true
         try {
            const res = await this.$axios.get(`/proposals/${this.id}/contracts`)
            this.linkedContracts = res.data.vertex_contracts || []
         } catch (e) {
            console.warn('Failed to fetch linked contracts:', e)
            this.linkedContracts = []
         } finally {
            this.isLoadingContracts = false
         }
      },

      openContractInNewTab(contract) {
         const routeData = this.$router.resolve({ name: 'contract-view', params: { id: contract.id } })
         window.open(routeData.href, '_blank')
      },

      getContractStatusColor(status) {
         switch (status) {
            case 0: return '#6C757D'
            case 1: return '#00A0C1'
            case 2: return '#17A2B8'
            case 3: return '#FFC107'
            case 4: return '#28A745'
            case 5: return '#DC3545'
            case 6: return '#FD7E14'
            case 7: return '#20C997'
            case 8: return '#DC3545'
            case 9: return '#DC3545'
            default: return '#6C757D'
         }
      },

      getContractStatusText(status) {
         switch (status) {
            case 0: return 'Draft'
            case 1: return 'Sent'
            case 2: return 'Viewed'
            case 3: return 'In Review'
            case 4: return 'Executed'
            case 5: return 'Cancelled'
            case 6: return 'Pending Approval'
            case 7: return 'Approved'
            case 8: return 'Declined'
            case 9: return 'Expired'
            default: return 'Draft'
         }
      },

      renderContent() {
         this.$refs.proposalDetailsFormRef.triggerDebounce()
      },

      formatDate(date) {
         return moment(date).format("DD-MMM-YYYY")
      },

      truncateLongText(text, limit) {
         return truncateLongText(text, limit)
      },

      readSectionsFromEditor() {
         const idMap = this.SECTION_ID_MAP();
         return this.proposalSections.map((section, idx) => {
            let editorContent = section.content || '';
            if (!section.is_hidden) {
               const sectionId = idMap[section.type] || ('start_' + section.type.toLowerCase() + '_section');
               editorContent = this.getSectionContentById(sectionId) || section.content || '';
            }
            return { ...section, content: editorContent, sequence: idx + 1 };
         });
      },

      SECTION_ID_MAP() {
         return {
            CoverPage:                  'start_cover_section',
            ProductService:             'start_product_services_section',
            PricingTableIntro:          'start_pricing_table_intro',
            PricingTable:               'start_pricing_table_section',
            PricingTableNotes:          'start_pricing_table_notes',
            ImageGrid:                  'start_image_grid_section',
            Testimonials:               'start_testimonial_main',
            SocialImpact:               'start_content_social_impact_section',
            Commitment:                 'start_content_commitment_section',
            TermsConditions:            'start_content_terms_condition_section',
            NextSteps:                  'start_content_next_steps_section',
         };
      },

      formatSectionLabel(type) {
         const labels = {
            CoverPage: 'Cover Page Section',
            ProductService: 'Products & Services Section',
            PricingTable: 'Pricing Table Section',
            PricingTableIntro: 'Pricing Table Intro Section',
            PricingTableNotes: 'Pricing Table Notes Section',
            ImageGrid: 'Image Grid Section',
            Testimonials: 'Testimonials Section',
            SocialImpact: 'Social Impact Section',
            Commitment: 'Commitment to Safety and Compliance Section',
            WHS: 'WHS Responsibilities Section',
            TermsConditions: 'Terms & Conditions Section',
            NextSteps: 'Next Steps Section',
         };
         return labels[type] || (type + ' Section');
      },

      onSubmit() {

      },

      viewProposal() {
         let ukey = this.$refs.proposalDetailsFormRef.getUniqueKey()
         let access_key = this.$refs.proposalDetailsFormRef.getInternalAccessKey()
         const routeData = this.$router.resolve({ name: 'clientProposal', params: { unique_key: ukey, access_key: access_key } });
         window.open(routeData.href, '_blank');
      },

      ChangeOption(isOption) {
         this.isOption = isOption
      },

      getSectionContentById(section_id) {
         return this.$refs.editorRef.getSectionContentById(section_id)
      },

      replaceVariableFields(content) {
         if (isEmpty(content)) {
            return content
         }

         let prop_lead = this.$refs.proposalDetailsFormRef ? this.$refs.proposalDetailsFormRef.lead : null;
         if (!prop_lead) return content;

         // Get BDM data for relations_manager resolution
         let prop_bdm = this.$refs.proposalDetailsFormRef ? this.$refs.proposalDetailsFormRef.bdm : null;

         let replacedContent = content;

         this.variableFieldsMapping.forEach(vfm => {
            // Extract the simple variable key from the replace_variable property
            let variableId = vfm.replace_variable;
            if (variableId === 'phone_number') variableId = 'contact_number';
            if (variableId === 'email') variableId = 'billing_email';
            
            // Resolve the value: relations_manager comes from BDM, others from lead
            let val;
            if (vfm.replace_variable === 'relations_manager') {
               if (prop_bdm && (prop_bdm.first_name || prop_bdm.last_name)) {
                  val = ((prop_bdm.first_name || '') + ' ' + (prop_bdm.last_name || '')).trim();
               } else {
                  val = 'Martin Boyd';
               }
            } else if (vfm.replace_variable === 'abn') {
               val = prop_lead.abn;
            } else if (vfm.replace_variable === 'business_number') {
               val = prop_lead.business_number || prop_lead.abn;
            } else if (vfm.replace_variable === 'business_number_type') {
               val = prop_lead.business_number_type || (isAustralianCountry(prop_lead.country) ? 'ABN' : 'Business Number');
            } else if (vfm.replace_variable === 'business_number_formatted') {
               const bNum = prop_lead.business_number || prop_lead.abn;
               const bType = prop_lead.business_number_type || (isAustralianCountry(prop_lead.country) ? 'ABN' : 'Business Number');
               val = bNum ? `${bType}: ${bNum}` : '';
            } else if (vfm.replace_variable === 'org_address') {
               val = ""; // Default empty
               let acceptances = this.proposal && this.proposal.contract_acceptances ? this.proposal.contract_acceptances : [];
               if (acceptances.length > 0) {
                  let acc = acceptances[acceptances.length - 1];
                  let parts = [];
                  if (acc.address_1 && acc.address_1.trim() !== '') parts.push(acc.address_1.trim());
                  if (acc.address_2 && acc.address_2.trim() !== '') parts.push(acc.address_2.trim());
                  if (acc.city && acc.city.trim() !== '') parts.push(acc.city.trim());
                  if (acc.state && acc.state.trim() !== '') parts.push(acc.state.trim());
                  if (acc.post_code && acc.post_code.trim() !== '') parts.push(acc.post_code.trim());
                  if (acc.country && acc.country.trim() !== '') parts.push(acc.country.trim());
                  
                  if (parts.length > 0) {
                     val = parts.join(', ');
                  } else if (acc.address && acc.address.trim() !== '') {
                     val = acc.address.trim();
                  }
               }
            } else {
               val = prop_lead[vfm.replace_variable];
            }

            let fillText = "<b>{No Record}</b>";
            if (!isEmpty(val)) {
                fillText = "<b>" + val + "</b>";
            } else if (vfm.replace_variable === 'abn' || vfm.replace_variable === 'business_number') {
                fillText = "<b>N/A</b>";
            } else if (vfm.replace_variable === 'org_address') {
                fillText = " ";
            }

            // Escape variableId for use in regex
            let safeVarId = variableId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            
            // Primary regex: try to replace the entire <mark> block if it wraps just this variable cleanly
            let rgxMark = new RegExp(`<mark[^>]*>\\s*\\{\\{\\s*<span[^>]*>!\\*<\\/span>\\s*<span id="variable_value_${safeVarId}"[^>]*>.*?<\\/span>\\s*<span[^>]*>\\*!<\\/span>\\s*\\}\\}\\s*<\\/mark>`, 'gi');
            
            // Fallback regex: replaces just the inner {{ ... }} if nested inside other marks (like first/last name blocks)
            let rgxFallback = new RegExp(`\\{\\{[^{}]*id="variable_value_${safeVarId}"[^{}]*\\}\\}`, 'gi');

            if (rgxMark.test(replacedContent)) {
               replacedContent = replacedContent.replace(rgxMark, fillText);
            } else {
               replacedContent = replacedContent.replace(rgxFallback, fillText);
            }
         });

         return replacedContent
      },

      // Add these inside your methods: {} object

      getStatusColor(status_code) {
         switch (status_code) {
            case 0: // Pending
               return this.colorForStatus.pending;
            case 1: // Sent
               return this.colorForStatus.sent;
            case 2: // Resent
               return this.colorForStatus.resent;
            case 3: // Accepted
               return this.colorForStatus.accepted;
            case 4: // Rejected
               return this.colorForStatus.rejected;
            case 5: // Expired
               return this.colorForStatus.rejected;
            case 6: // Completed
               return '#6C757D';
            default:
               return this.colorForStatus.pending;
         }
      },

      getStatusText(status_code) {
         switch (status_code) {
            case 1:
               return "Sent";
            case 2:
               return "Resent";
            case 3:
               return "Accepted";
            case 4:
               return "Declined";
            case 5:
               return "Expired";
            case 6:
               return "Completed";
            default: // Handles 0 and any other case
               return "Draft";
         }
      },

      getProposalStatusBadgeClass(statusCode) {
         switch (Number(statusCode)) {
            case 1: return 'spark-pill-badge--sent';
            case 2: return 'spark-pill-badge--resent';
            case 3: return 'spark-pill-badge--accepted';
            case 4: return 'spark-pill-badge--declined';
            case 5: return 'spark-pill-badge--expired';
            case 6: return 'spark-pill-badge--completed';
            default: return 'spark-pill-badge--draft';
         }
      },

      getVersionBadgeClass(versionNumber) {
         if (!versionNumber) return 'spark-pill-badge--version-major';
         const clean = String(versionNumber).startsWith('v') ? String(versionNumber).slice(1) : String(versionNumber);
         const parts = clean.split('.');
         if (parts.length >= 2) {
            const minor = parseInt(parts[1], 10);
            if (!isNaN(minor)) {
               return minor > 0 ? 'spark-pill-badge--version-minor' : 'spark-pill-badge--version-major';
            }
         }
         return 'spark-pill-badge--version-major';
      },

      getProposalStatusIcon(statusCode) {
         switch (Number(statusCode)) {
            case 1: return 'fa-solid fa-paper-plane';
            case 2: return 'fa-solid fa-rotate-right';
            case 3: return 'fa-solid fa-circle-check';
            case 4: return 'fa-solid fa-circle-xmark';
            case 5: return 'fa-solid fa-calendar-xmark';
            case 6: return 'fa-solid fa-check-double';
            default: return 'fa-solid fa-pen';
         }
      },
   }
})

</script>
<style scoped>
.nav-tabs .nav-link.active {
   background-color: #00A0C1;
   color: #fff !important;
   border-color: #00A0C1;
}

.nav-tabs .nav-link {
   color: black;
}

.audit-menu-tab {
   border: none;
   /* margin: 1rem; */
}

.audit-menu-tab .nav-link {
   color: #435666;
   height: 40px;
   width: 200px;
   min-width: 95px;
   /* padding: 1rem; */
   font-weight: 400;
   border: 1px solid #A3C0D1;
}

button.nav-link {
   border-radius: 0px !important;
}

.audit-menu-tab .nav-link.active {
   background-color: #00A0C1;
   color: #fff;
   font-weight: 900;
}

.audit-header-date {
   justify-content: start;
}

@media (max-width: 767px) {

   .audit-menu-tab .nav-link {
      width: auto;
      height: auto;
   }

   .activity-log-button,
   .activity-log {
      display: none;
   }

}

@media (max-width: 575px) {
   .audit-header {
      flex-direction: column-reverse;
   }

   .audit-header-date {
      justify-content: center;
   }

}

.editor {
   display: flex;
}

.toolbar {
   /* Toolbar styles */
   display: flex;
   flex-direction: column;
}

.content {
   /* Content area styles */
   margin-left: 20px;
}

.cover-page {
   /* Cover page styles */
   width: 500px;
   height: 300px;
   border: 1px solid #ccc;
   position: relative;
}

.cover-image {
   /* Cover image styles */
   width: 100%;
   height: 100%;
   object-fit: cover;
}

.logo {
   /* Logo styles */
   position: absolute;
   top: 10px;
   left: 10px;
}

.cover-text {
   /* Cover text styles */
   position: absolute;
   bottom: 10px;
   left: 10px;
   color: white;
}

.details {
   /* Details section styles */
   margin-top: 20px;
}

.detail-item {
   /* Detail item styles */
   margin-bottom: 10px;
}

.block-button-container {
   display: flex;
   align-items: center;
   /* Vertically align icons */
   gap: 15px;
   /* Adjust spacing between buttons */
}

@media (min-width: 1900px) {

   /* Adjust breakpoint as needed */
   .block-button-container {
      gap: 6px;
      /* Larger gap for larger screens */
   }
}

.collapsible-content {
   padding: 10px;
   margin-top: 10px;
   overflow: hidden;
   transition: width 0.3s ease;
   /* Float the element to the right */
}

.collapse-transition {
   transition: width 0.3s ease;
}



.hidden {
   display: none;
}

/* Scroll bar */
/* width */
::-webkit-scrollbar {
   width: 10px;
}

/* Track */
::-webkit-scrollbar-track {
   background: #f1f1f1;
}

/* Handle */
::-webkit-scrollbar-thumb {
   background: #00a0c1;
}

/* Handle on hover */
::-webkit-scrollbar-thumb:hover {
   background: #00a0c1;
}

.version-status-icon {
   display: inline-flex;
   justify-content: center;
   align-items: center;
   min-width: 45px;
   height: 24px;
   padding: 0 10px;
   /* A bit more padding for status text */
   border-radius: 3px;
   color: white;
   font-size: 14px;
   /* Slightly larger font for the title area */
   font-weight: bold;
   text-transform: none;
   white-space: nowrap;
   vertical-align: middle;
   /* Align nicely with the title text */
   box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

/* --- PDF SPECIFIC STYLES (REVISED) --- */
#pdf-content-area {
   position: absolute;
   left: -9999px;
   top: 0;
   width: 210mm;
   padding: 20mm;
   box-sizing: border-box;
   background-color: white;
   font-family: 'Helvetica', 'Arial', sans-serif;
   /* Use PDF-safe fonts */
   font-size: 11pt;
   line-height: 1.5;
   color: #333333;
}

/* This is our new container for each major section */
.pdf-page-section {
   page-break-inside: avoid !important;
   /* TRY NOT to break this section */
   page-break-after: auto !important;
   margin-bottom: 15px;
   /* Add some space between sections */
}

/* Tame large images inside the PDF content */
#pdf-content-area img {
   max-width: 100% !important;
   height: auto !important;
   page-break-inside: avoid !important;
}

#pdf-content-area p,
#pdf-content-area li,
#pdf-content-area .pdf-product-table tr {
   page-break-inside: avoid !important;
}

#pdf-content-area h1,
#pdf-content-area h2,
#pdf-content-area h3,
#pdf-content-area h4 {
   color: #00a0c1;
   line-height: 1.2;
   page-break-inside: avoid !important;
   /* This rule prevents a heading from being the last line on a page */
   page-break-after: avoid !important;
}

#pdf-content-area h2.pdf-title {
   font-size: 24pt;
   text-align: center;
   margin-bottom: 1.5em;
}

#pdf-content-area h4.pdf-section-title {
   font-size: 14pt;
   border-bottom: 2px solid #00a0c1;
   padding-bottom: 5px;
   margin-bottom: 1em;
}

.pdf-product-table {
   width: 100%;
   border-collapse: collapse;
   margin: 20px 0;
   border: 1px solid #cccccc;
   page-break-inside: auto;
   /* A table itself is allowed to break */
}

.pdf-product-table th,
.pdf-product-table td {
   padding: 8px 12px;
   border: 1px solid #e0e0e0;
   text-align: left;
   font-size: 10pt;
}

.pdf-product-table th {
   background-color: #00a0c1;
   color: white;
   font-weight: bold;
}

.pdf-product-table tfoot .pdf-total-row {
   font-weight: bold;
   background-color: #f5f5f5;
}

/* ADD THIS TO FIX THE ISSUE OF PRICING TABLE PUSHING CONTENT DOWN ON SMALLER SCREENS */
@media (max-width: 1499px) {
   .collapsible-content {
      display: block;
      /* Use block display to prevent flexbox from wrapping the column down */
   }

   .proposal-variable-fields,
   .proposal-details-form,
   .proposal-activity {
      width: 100%;
      /* Ensure each form/component takes full width */
      margin-bottom: 15px;
      /* Add some spacing between sections */
   }
}

/*
  TARGET: The <button> element, which acts as our circular container.
*/
.btn-svg-icon {
   width: 30px;
   /* 👈 Increased from 34px */
   height: 30px;
   /* 👈 Increased from 34px */
   border-radius: 50%;
   /* This makes the button a circle */

   /* These three lines perfectly center the icon inside the circle */
   display: flex;
   align-items: center;
   justify-content: center;

   /* A few resets and a smooth transition for the hover effect */
   background-color: transparent;
   border: none;
   padding: 0;
   cursor: pointer;
   transition: background-color 0.2s ease;
}

/*
  TARGET: The <img> icon itself.
  PURPOSE: Make the icon smaller than the button to create a padding effect.
*/
.icon-svg {
   width: 30px;
   /* 👈 Increased from 20px */
   height: 30px;
   /* 👈 Increased from 20px */
}


/* --- THE HOVER EFFECT --- */

/*
  Step 1: Change the BUTTON's background on hover.
  Because the button has border-radius: 50%, the background will be a circle.
*/
.btn-svg-icon:hover {
   background-color: #00A0C1;
   /* The solid teal color */
}

/*
  Step 2: Change the ICON's color when the parent button is hovered.
  The filter trick makes your teal SVG icon appear white.
*/
.btn-svg-icon:hover .icon-svg {
   filter: brightness(0) invert(1);
}

/*
  TARGET: The button when it is disabled.
  PURPOSE: Change the button's appearance to a gray color and change the cursor.
*/
.btn-svg-icon:disabled {
   background-color: #f0f0f0;
   /* A light gray background */
   cursor: not-allowed;
   transition: none;
   /* Disable the transition to prevent the hover effect from flashing */
}

/*
  TARGET: The icon inside the disabled button.
  PURPOSE: Change the icon's color to a darker gray.
*/
.btn-svg-icon:disabled .icon-svg {
   filter: grayscale(100%) brightness(50%);
   /* Make the icon gray */
}

/*
  TARGET: Override the hover effect for the disabled button.
  PURPOSE: Ensure no color change occurs when hovering over the disabled button.
*/
.btn-svg-icon:disabled:hover {
   background-color: #f0f0f0;
   /* Keeps the same gray background */
   cursor: not-allowed;
}

/*
  TARGET: The icon inside the disabled button on hover.
  PURPOSE: Ensure the icon's color does not change on hover.
*/
.btn-svg-icon:disabled:hover .icon-svg {
   filter: grayscale(100%) brightness(50%);
   /* Keeps the same gray icon color */
}

/* Custom Refresh Pricing Pill Button */
.btn-pill-icon {
   height: 30px;
   padding: 0 16px;
   margin-right: 5px;
   border-radius: 15px; /* Pill shape */
   background-color: transparent;
   border: 1px solid #00A0C1;
   color: #00A0C1;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   font-size: 13px;
   font-weight: 500;
   white-space: nowrap;
   cursor: pointer;
   transition: all 0.2s ease-in-out;
}

.btn-pill-icon:hover {
   background-color: #00A0C1;
   color: white;
}

.btn-pill-icon:disabled {
   background-color: #f0f0f0;
   border-color: #ccc;
   color: #999;
   cursor: not-allowed;
}

.btn-pill-icon i {
   margin-right: 6px;
}

/* ===== Linked Contracts Tab ===== */
.linked-contracts-badge {
   display: inline-flex;
   align-items: center;
   justify-content: center;
   min-width: 18px;
   height: 18px;
   padding: 0 5px;
   margin-left: 6px;
   border-radius: 9px;
   background-color: #00A0C1;
   color: white;
   font-size: 11px;
   font-weight: 700;
   line-height: 1;
}

.linked-contracts-list {
   display: flex;
   flex-direction: column;
   gap: 10px;
}

.linked-contract-card {
   border: 1px solid #e2e8f0;
   border-radius: 10px;
   padding: 14px 16px;
   transition: all 0.15s ease;
}
.linked-contract-card:hover {
   border-color: #00A0C1;
   box-shadow: 0 2px 10px rgba(0, 160, 193, 0.10);
}

.linked-contract-status {
   display: inline-block;
   padding: 2px 8px;
   border-radius: 4px;
   color: white;
   font-size: 11px;
   font-weight: 600;
   white-space: nowrap;
}
</style>